import React, { useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { MerchCard } from "@/components/site/MerchCard";
import { Reveal, RevealGroup, RevealItem } from "@/components/site/Reveal";
import { useSite } from "@/context/SiteContext";

const isSongMerch = (p) => /distrokid\.com/i.test(p.fulfillment_url || "");

const TABS = [
  { key: "all", label: "All", hint: "Everything in the store" },
  { key: "label", label: "Label Merch", hint: "Heart N Soul Records logo pieces" },
  { key: "song", label: "Song Merch", hint: "Designs from the singles" },
];

export default function Merch() {
  const { merch } = useSite();
  const [params, setParams] = useSearchParams();
  const tab = TABS.some((t) => t.key === params.get("tab")) ? params.get("tab") : "all";
  const setTab = (key) => setParams(key === "all" ? {} : { tab: key }, { replace: true });

  const counts = useMemo(() => ({
    all: merch.length,
    song: merch.filter(isSongMerch).length,
    label: merch.filter((p) => !isSongMerch(p)).length,
  }), [merch]);

  const shown = tab === "all" ? merch : merch.filter((p) => (tab === "song") === isSongMerch(p));
  const active = TABS.find((t) => t.key === tab);

  return (
    <main className="pt-[74px]" data-testid="merch-page">
      <section className="section-pad">
        <div className="mx-auto max-w-[1360px] px-5 sm:px-8">
          <Reveal>
            <p className="font-caps text-gold" style={{ fontSize: ".72rem" }}>Products by HNS Records</p>
            <h1 className="font-display foil-text mt-3" style={{ fontSize: "var(--t-hero)", lineHeight: 1 }}>Order Yours Today</h1>
            <p className="mt-5 max-w-xl" style={{ color: "var(--ash)" }}>Label-branded pieces cut from the same cloth as the music — engraved, warm, and made to last.</p>
          </Reveal>

          <div className="mt-12 flex flex-wrap items-center gap-3" role="tablist" aria-label="Merch collections" data-testid="merch-filter-tabs">
            {TABS.map((t) => {
              const on = t.key === tab;
              return (
                <button
                  key={t.key}
                  role="tab"
                  aria-selected={on}
                  onClick={() => setTab(t.key)}
                  className="font-caps"
                  style={{
                    fontSize: ".62rem",
                    padding: ".6rem 1.1rem",
                    borderRadius: "var(--radius)",
                    border: `1px solid ${on ? "var(--gold)" : "rgba(200,155,60,.3)"}`,
                    color: on ? "var(--ink)" : "var(--parchment)",
                    background: on ? "var(--gold)" : "transparent",
                    display: "inline-flex",
                    alignItems: "center",
                    gap: ".5rem",
                    transition: "background-color .2s, color .2s, border-color .2s",
                  }}
                  data-testid={`merch-tab-${t.key}`}
                >
                  <span>{t.label}</span>
                  <span style={{ opacity: on ? .7 : .5, fontSize: ".55rem" }} data-testid={`merch-tab-count-${t.key}`}>{counts[t.key]}</span>
                </button>
              );
            })}
            <span className="font-caps text-ash ml-1" style={{ fontSize: ".58rem" }} data-testid="merch-tab-hint">{active.hint}</span>
          </div>

          <RevealGroup key={tab} className="grid grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6 mt-8" stagger={0.05}>
            {shown.map((p) => <RevealItem key={p.id}><MerchCard product={p} showSizes /></RevealItem>)}
          </RevealGroup>

          {shown.length === 0 && (
            <p className="mt-10 font-caps text-ash" style={{ fontSize: ".62rem" }} data-testid="merch-empty">Nothing here yet — check back soon.</p>
          )}

          <p className="mt-12 font-caps text-ash" style={{ fontSize: ".62rem" }}>Secure Stripe checkout. Free US shipping over $75.</p>
        </div>
      </section>
    </main>
  );
}
